import CachedIcon from '@mui/icons-material/Cached';
import { CircularProgress, IconButton, Tooltip } from '@mui/material';
import { DateTime } from 'luxon';
import { LoadingButton } from 'probonio-shared-ui/component/button';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  isLoading: boolean;
  onClick: () => void;
  dataTestId?: string;
  disableMinutesTTL?: number;
  lastSyncedAt?: string;
  /** Renders a bare icon button (no label) instead of the default full-width text button. */
  iconOnly?: boolean;
  iconSize?: 'small' | 'medium';
}

export const SyncButton: React.FC<Props> = ({
  isLoading,
  onClick,
  dataTestId,
  lastSyncedAt,
  disableMinutesTTL = 1,
  iconOnly,
  iconSize = 'small',
}: Props) => {
  const REFRESH_TIMER = 1000;
  const [isSyncDisabled, setIsSyncDisabled] = useState<boolean>(false);
  const { t } = useTranslation('common');

  useEffect(() => {
    const checkSyncStatus = () => {
      const currentTime = DateTime.now();
      const lastSyncedTime = lastSyncedAt ? DateTime.fromISO(lastSyncedAt) : undefined;
      const isDisabled = lastSyncedTime && currentTime.diff(lastSyncedTime, 'minutes').minutes < disableMinutesTTL;

      setIsSyncDisabled(!!isDisabled);
    };

    checkSyncStatus();

    const intervalId = setInterval(checkSyncStatus, REFRESH_TIMER);

    return () => clearInterval(intervalId);
  }, [disableMinutesTTL, lastSyncedAt]);

  let toolTipTitle: string | undefined;
  if (isSyncDisabled) {
    toolTipTitle = t('buttons.syncNextToolTip', {
      date: DateTime.fromISO(lastSyncedAt!).plus({ minutes: disableMinutesTTL }).toJSDate(),
    });
  } else if (iconOnly) {
    toolTipTitle = t('buttons.syncNow');
  }

  if (iconOnly) {
    return (
      <Tooltip title={toolTipTitle}>
        <span>
          <IconButton size="small" data-test-id={dataTestId} onClick={onClick} disabled={isSyncDisabled} aria-label={t('buttons.syncNow')}>
            {isLoading ? <CircularProgress size={18} /> : <CachedIcon fontSize={iconSize} />}
          </IconButton>
        </span>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={toolTipTitle}>
      <span>
        <LoadingButton
          dataTestId={dataTestId}
          fullWidth
          color="secondary"
          loading={isLoading}
          onClick={onClick}
          disabled={isSyncDisabled}
          startIcon={<CachedIcon />}
        >
          {t('buttons.syncNow')}
        </LoadingButton>
      </span>
    </Tooltip>
  );
};
